(ns hai.chats.components.interface
  "Main chat interface with sidebar and chat view"
  (:require
   [garden.stylesheet]
   [hai.chats.components.message :as message]
   [hai.ui.components :as c]
   [hai.ui.components.new-design.button :as button]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [hai.chats.api :as api]
   [lambdaisland.deja-fu :as fu]
   [lambdaisland.ornament :as o]
   [reagent.core :as reagent]
   [hai.ui.components.form.select.single-select :as single-select]
   [lambdaisland.glogi :as log]
   [clojure.string :as str]))

;; Empty chat view - shown when no chat is selected
(o/defstyled empty-chat-view :div
  :flex-1 :flex :flex-col :justify-center :items-center :text-center :p-6

  [:.container
   :flex :flex-col :w-full :gap-4
   {:max-width "600px"}]

  [:.emoji
   {:font-size "2rem"}]

  [:.heading
   :m-0
   {:font-size "1.5rem"}]

  [:.input-wrapper
   :w-full
   [:input
    :w-full :rounded :p-3 :border :text-base :bg-white :box-border
    {:border-color c/--gray-6}]]

  [:.controls :inline :bg-white
   [:select
    :rounded :p-2 :border :text-base :bg-white
    {:border-color c/--gray-6}]]

  ([_]
   (reagent/with-let [!assistants (<s [::api/available-assistants])
                      !current-route (<s [:routing/current-route])
                      !selected-assistant (reagent/atom nil)
                      !input-value (reagent/atom "")
                      on-send #(>e [::api/talk-new-stream {:content @!input-value
                                                            :base-id @!selected-assistant}])]
     (let [base-param (get-in @!current-route [:query-params :ai-model-config])
           preselected-uuid (when base-param (parse-uuid base-param))]
       ;; Set preselected value if not already set and param exists
       (when (and preselected-uuid (nil? @!selected-assistant))
         (reset! !selected-assistant preselected-uuid))
       [:<>
        [:div.container
         [:div.emoji "💬"]
         [c/Heading {:size "6" :mb "4"} "What can I help you with today?"]
         [c/Flex {:justify "start"}
          [:div.controls
           (let [assistants @!assistants]
             (cond
               (nil? assistants)
               nil
               (empty? assistants)
               [c/Button
                {:on-click #(>e [:assistant/create {}])}
                "Attach knowledge base (create your first)"]
               :else
               [:<>
                [single-select/field
                 {:form.field/current-value (if @!selected-assistant
                                              @!selected-assistant
                                              nil)
                  :form.field/full-width? false
                  :form.field/placeholder "Select a task or assistant"
                  :form.field/set-value! (fn [e]
                                           (log/info :set-value e)
                                           (reset! !selected-assistant e))
                  :form.field.select/options (sort-by :title
                                                      (map (fn [item]
                                                             {:value (:ai-model-config/uuid item)
                                                              :title (:ai-model-config/title item)})
                                                           (vals assistants)))}]]))]]
         [:div.input-wrapper
          [c/Flex {:direction "column" :gap "2"}
           [c/TextArea {:type "text"
                        :size "3"
                        :radius "full"
                        :resize "vertical"
                        :placeholder "How can I help you today?"
                        :on-change #(reset! !input-value (-> % .-target .-value))
                        :on-key-down (fn [e]
                                       (when (and (= (.-key e) "Enter")
                                                  (not (.-shiftKey e)))
                                         (.preventDefault e)
                                         (when on-send
                                           (on-send))
                                         (set! (.-value (.-target e)) "")))}]
           [c/Flex {:gap "2" :justify "end"}
            [c/Button
             {:disabled (str/blank? @!input-value)
              :on-click on-send}
             [icons/outlined-send]
             "Chat"
             ]]]]]]))))

(def settings-icon (icons/mui-icon :outlined :settings))

(o/defstyled actions-box :div
  :flex :items-center :gap-2 :shrink-0)

;; Active chat view component
(o/defstyled active-chat-view :div
  :flex-1 :flex :flex-col :overflow-y-auto
  {:background c/--gray-2}

  [:.header
   :border-b :flex :items-center :justify-between
   {:border-color c/--gray-6
    :padding "6px 6px 5.5px 6px"
    :background t/--bg-2}]

  [:.messages-wrapper :flex :flex-1 :overflow-y-auto :p-3 :gap-2
   {:scroll-behavior "smooth"}]
  [:.messages :flex :flex-1 :flex-col :max-w-5xl :mx-auto
   [:.streaming :pl-4 :flex :items-center :gap-2
    [:.dots :flex :gap-1 :items-center
     [:.dot :w-2 :h-2 :rounded-full
      {:background c/--gray-9
       :animation "dotFade 1.4s ease-in-out infinite"}]
     [:.dot:nth-child-1
      {:animation-delay "0s"}]
     [:.dot:nth-child-2
      {:animation-delay "0.2s"}]
     [:.dot:nth-child-3
      {:animation-delay "0.4s"}]]
    [:svg :w-8
     {:animation "logoSpin 2s ease-in-out infinite"}]]]

  [:.empty-state
   :text-center :italic :p-4
   {:color c/--gray-11}]

  [:.input-area
   :p-3 :border-t
   {:border-color c/--gray-6
    :background c/--color-surface}]
  ([{:keys [chat !messages !chat-query-state]}]
   (reagent/with-let [messages-ref (atom nil)
                      !streaming? (<s [::api/streaming?])
                      prev-msg-count (atom (count @!messages))
                      scroll-to-bottom (fn []
                                         (when-let [el @messages-ref]
                                           (js/setTimeout
                                            #(set! (.-scrollTop el) (.-scrollHeight el))
                                            10)))]
     ;; Watch for message changes
     (let [current-count (count @!messages)]
       (when (not= current-count @prev-msg-count)
         (reset! prev-msg-count current-count)
         (scroll-to-bottom)))

     [:<>
      (let [assistant-id (get-in chat [:chat/ai-model-config :ai-model-config/uuid])
            match @(<s [:routing/route-details :tasks/knowledge-config {:ai-model-config/uuid assistant-id}])]
        [:div.header
         [layout/breadcrumbs {:breadcrumbs [{:route [:tasks/knowledge-config
                                                     {:ai-model-config/uuid assistant-id}]
                                             :label (get-in chat [:chat/ai-model-config :ai-model-config/title])}
                                            (:chat/name chat)]}]
         [actions-box
          [c/Link {:href (:path match) :title "Go to settings"}
           [settings-icon {:style {:width "24px" :height "24px"}}]]
          [button/light-small
           {:on-click (fn [_]
                        (let [query-params nil
                              fragment "sharing"]
                          (>e [:routing/navigate [:tasks/knowledge-config {:ai-model-config/uuid assistant-id} query-params fragment]])))
            :text     "Share assistant"
            :title    "Change who has access to use this assistant"}]]])
      [:div.messages-wrapper
       {:ref (fn [el]
               (when el
                 (reset! messages-ref el)
                 (scroll-to-bottom)))}
       [:div.messages
        (let [messages @!messages
              msg-count (count messages)
              streaming? @!streaming?]
          (if (empty? messages)
            [:div.empty-state "Start the conversation..."]
            (doall
             (map-indexed
              (fn [idx msg]
                (let [is-last? (= idx (dec msg-count))
                      is-assistant? (= (:message/role msg) :message.role/assistant)
                      show-streaming-cursor? (and streaming? is-last? is-assistant?)]
                  ^{:key (or (:message/uuid msg) (hash msg))}
                  [message/message
                   {:role (:message/role msg)
                    :content (:message/content msg)
                    :timestamp (:message/created-at msg)
                    :streaming? show-streaming-cursor?}]))
              messages))))
        (when @!streaming?
          [:div.streaming
           [:img {:src "/imgs/humble-avatar-blink.gif"
                  :alt "Humble AI"
                  :width "24"
                  :height "24"
                  :style {:border-radius "6px"}}]
           [:div.dots
            [:div.dot]
            [:div.dot]
            [:div.dot]]])]]
      [:div.input-area
       [message/message-input
        {:on-send (fn [content]
                    (>e [::api/talk-stream
                         {#_#_:chat-uuid (:chat/uuid chat)
                          :base-id (-> chat
                                       :chat/ai-model-config
                                       :ai-model-config/uuid)
                          :content content}]))}]]])))

;; Chat sidebar item component
(o/defstyled chat-sidebar-item :div
  :cursor-pointer :rounded :p-3 :mb-1
  {:transition "all 0.2s ease"
   :border-left "3px solid transparent"}

  [:&:hover
   {:background c/--gray-2}]

  [:&.active
   {:background c/--accent-3
    :border-left-color c/--accent-9}]

  [:.title
   :mb-1 :text-sm
   {:color c/--gray-12}]

  [:&.active
   [:.title
    :font-semibold]]

  [:.metadata
   :flex :justify-between :text-xs :gap-2
   {:color c/--gray-11}]

  ([{:keys [chat active? on-click]}]
   [:<>
    {:class (when active? "active")
     :on-click on-click}
    [:div.title
     (or (:chat/name chat) "Untitled Chat")]
    [:div.metadata
     (when (:chat/updated-at chat)
       [:span (fu/format (:chat/updated-at chat) "dd MMM")])]]))

;; Chat sidebar component
(o/defstyled chat-sidebar :aside
  :flex :flex-col :bg-white :h-screen :border-r-2
  [:svg :fill-current]
  {:width "300px"
   :border-color c/--gray-6
   :box-shadow "2px 0 4px rgba(0, 0, 0, 0.05)"}
  [:.header :p-4 :border-b :flex :flex-column :justify-between
   :items-center
   [:>h2 :mb-0]
   {:border-color c/--gray-6}]
  [:.sidebar-title :m-0 :font-semibold :text-xl :mb-2]
  [:.new-chat-button]
  [:.chat-list :flex-1 :overflow-y-auto :p-2]
  [:.empty-state :text-center :p-4
   {:color c/--gray-11}]
  [:.empty-message :m-0 :mb-1 :text-base]
  [:.empty-subtext :m-0 :text-sm
   {:opacity 0.8}]
  ([{:keys [chats active-chat-id on-select-chat on-new-chat]}]
   [:<>
    [:div.header
     [:h2.sidebar-title "My Assistant"]
     [:div.new-chat-button
      [button/light-small
       {:on-click on-new-chat
        :text     "New chat"
        ;:icon [icons/outlined-create]
        }
       ]]]
    [:div.chat-list
     (cond
       (nil? chats)
       [c/Flex {:justify "center" :py "9"}
        [c/Spinner {:size "3"}]]
       (empty? chats)
       [:div.empty-state
        [:p.empty-message "No chats yet"]
        [:p.empty-subtext "Create your first chat to get started"]]
       :else
       (for [chat chats]
         ^{:key (:chat/uuid chat)}
         [chat-sidebar-item {:chat chat
                             :active? (= (:chat/uuid chat) active-chat-id)
                             :on-click #(on-select-chat (:chat/uuid chat))}]))]]))

;; Chat interface component - main content area
(o/defstyled chat-interface :main
  :flex-1 :flex :flex-col :overflow-hidden :h-screen
  {:background c/--gray-2}

  ([{:keys [chat new?]}]
   (reagent/with-let [!new-chat-first-message (<s [::api/new-chat-first-message])
                      !messages (<s [::api/active-messages])
                      !chat-query-state (<s [:fetch/state ::api/talk])]
     [:<>
      (cond
        new?
        [empty-chat-view]

        :else
        ;; (and @!messages (seq @!messages))
        [active-chat-view {:chat chat :!messages !messages :!chat-query-state !chat-query-state}]

        ;; (and new? @!new-chat-first-message)
        ;; [active-chat-view {:new? true :chat nil :!messages !messages}]
        )])))

;; Main chat layout component
(o/defstyled chat-layout :div
  :flex :w-full :overflow-hidden :h-screen
  [:svg :fill-current]

  ([{:keys [new?]
     :or {new? false}}]
   (reagent/with-let [!chats (<s [::api/all])
                      !active-chat (<s [::api/active-chat])]
     [:<>
      [chat-sidebar {:chats @!chats
                     :active-chat-id (:chat/uuid @!active-chat)
                     :on-select-chat (fn [chat-id]
                                       (>e [:routing/navigate [:tasks/chats-by-id {:id chat-id}]])
                                       (>e [::api/select-chat chat-id]))
                     :on-new-chat #(do
                                     (>e [:routing/navigate [:tasks/chats]])
                                     (>e [::api/select-chat nil]))}]
      [chat-interface {:chat @!active-chat :new? new?}]])))

(defn chat-page []
  [chat-layout {:new? false}])

(defn new-chat-page []
  [chat-layout {:new? true}])
